/**
 * The campaign: the player's run up the ladder, and what a loss does to it.
 *
 * **There is no game over in this module and there is not going to be one.**
 * A loss is a result like any other: it is recorded, the ladder position drops,
 * and the next fight is the one after it. Nothing here returns `failed`,
 * nothing offers a retry, and no caller is ever told to put the run back to the
 * start — which is deliberate rather than unfinished. A street fighting career
 * is a record with losses in it, and a game that ends on one is a game that
 * asks you to pretend the loss did not happen.
 *
 * What a loss *does* cost is position. You slip down the ladder, the contender
 * threshold gets further away, and the fight you were two wins from has to be
 * earned again. That is the whole of the punishment and it is enough.
 *
 * Pure, and it owns nothing: the ladder is passed in, and the ranks live there.
 * This module holds the record — what has been fought, what came of it, and
 * whether the act-ending fight is open yet.
 */

import { LADDER, validateLadder } from './ladder.js';
import { CARDS, buildOffer, ducking, validateCards, venueCard } from './cards.js';
import HEAT from '../core/heat.json';

/**
 * `heat` is a function rather than a number because the offer is made out of it
 * and the player's heat moves all round. It defaults to the resting value, so a
 * campaign built without one — every test that predates the purse, and every
 * headless run — offers the same fights at the same prices as before.
 */
export function createCampaign({
  ladder,
  config = LADDER,
  cards = CARDS,
  heat = () => HEAT.baseline,
} = {}) {
  if (!ladder) throw new Error('a campaign needs a ladder to climb');
  validateLadder(config);
  validateCards(cards);

  const playerId = ladder.playerId;
  const listeners = new Set();

  let fights = 0;
  let wins = 0;
  let losses = 0;
  let draws = 0;
  /** Positive on a run of wins, negative on a run of losses, 0 before any. */
  let streak = 0;
  /** Newest first, capped by `campaign.history`. */
  let history = [];
  /**
   * Who the player has fought, and how it went, for as long as the career
   * lasts.
   *
   * Separate from `history` because `history` is a *rolling* list — it is what
   * the screens show — and a head-to-head record is the opposite: it is only
   * worth anything if it remembers the fight eleven rounds ago. A fighter the
   * player has beaten twice has something different to say from one who has
   * never met them, and neither fact survives an eight-entry window.
   */
  const meetings = new Map();
  let actComplete = false;
  /**
   * The three fights on the table, and which of them was taken.
   *
   * Made once and held, rather than derived on every read. The ladder churns
   * while the player is in the ring (gotcha 45), and an offer that quietly
   * re-made itself between being shown and being accepted would be an offer
   * that was never really made. `record` throws both away: a new fight is a new
   * card.
   */
  let offer = null;
  let accepted = null;
  /** How many times in a row the reach has been turned down. Career-long. */
  let ducks = 0;

  const rank = () => ladder.rankOf(playerId);
  const threshold = config.contender.rank;

  /** Everybody the player could be matched with, best first. */
  const others = () => ladder.ranks.filter((entry) => entry.id !== playerId);

  /**
   * Who the act ends against: the `act.rank`-th fighter who is not the player.
   *
   * Counted past the player rather than at a fixed rank, because the ladder is
   * alive — by the time anybody gets there the fighter at the top may not be
   * the one who started there, and if the player has climbed over them it is
   * the next one down. The act is against whoever is holding the top, not
   * against a name written in the file.
   */
  function actOpponent() {
    const list = others();
    return list[(config.act.rank ?? 1) - 1] ?? list[list.length - 1] ?? null;
  }

  /** The next rung: whoever is directly above. Below the top, that is the climb. */
  function circuitOpponent() {
    const at = rank();
    return ladder.at(at - 1) ?? ladder.at(at + 1) ?? null;
  }

  function announce(event) {
    for (const listener of listeners) listener(event);
    return event;
  }

  /** The player's record against one fighter. Wins are the *player's*. */
  const against = (id) => {
    const met = meetings.get(id) ?? { wins: 0, losses: 0, draws: 0 };
    return Object.freeze({ id, ...met, met: met.wins + met.losses + (met.draws ?? 0) });
  };

  const campaign = {
    config,
    ladder,
    playerId,

    against,
    /** Everybody the player has fought, newest meeting last. For the screens. */
    get met() {
      return [...meetings.keys()].map(against);
    },

    get fights() {
      return fights;
    },
    get wins() {
      return wins;
    },
    get losses() {
      return losses;
    },
    get draws() {
      return draws;
    },
    get streak() {
      return streak;
    },
    /** Newest first. Losses are in here with everything else. */
    get history() {
      return [...history];
    },
    get rank() {
      return rank();
    },
    /**
     * How hot the player is right now.
     *
     * The campaign has been handed this since step 59 to price a purse with,
     * and nothing could read it back. Sala needs it too — a promoter's whole
     * opinion of a fighter is how many people turn up — and going through the
     * campaign rather than reaching for a combatant keeps `src/sim` free of
     * fighters, which is what makes all of it testable in `node`.
     */
    get heat() {
      return heat() ?? HEAT.baseline;
    },
    get best() {
      return history.reduce((top, entry) => Math.min(top, entry.rank), rank());
    },

    /**
     * The gate, and why it is shut.
     *
     * Returned as an object rather than a boolean because "locked" is not
     * useful on its own: the overlay has to be able to say *how far off* the
     * player is, and a player who cannot see the distance cannot see that
     * winning closed it.
     */
    get gate() {
      const at = rank();
      return Object.freeze({
        open: at <= threshold,
        rank: at,
        need: threshold,
        /** Places still to climb before the act-ending fight unlocks. */
        short: Math.max(0, at - threshold),
        describe: config.contender.describe,
      });
    },
    get contender() {
      return rank() <= threshold;
    },
    get actComplete() {
      return actComplete;
    },
    get act() {
      return Object.freeze({ ...config.act, opponent: actOpponent(), unlocked: rank() <= threshold });
    },

    /**
     * The three fights on the table.
     *
     * Built here rather than by the screen that shows them, for the same reason
     * `next` chooses the fight rather than its callers (gotcha 47): the
     * act-ending fight is one of these cards, and a card made outside the gate
     * is a way to be handed it from rank eleven. Made once and then held — see
     * `offer` above.
     */
    get offer() {
      offer ??= buildOffer({
        ladder,
        gate: campaign.gate,
        act: campaign.act,
        heat: heat(),
        config: cards,
      });
      return offer;
    },

    /** The card taken, or null while the player is still looking at three. */
    get accepted() {
      return accepted ? (campaign.offer.find((card) => card.slot === accepted) ?? null) : null;
    },

    /** How the crowd feels about the reach cards that have gone by. */
    get ducking() {
      return ducking(ducks, cards);
    },

    /**
     * The player takes one of the three.
     *
     * Returns what it cost, which for two of the three cards is the crowd's
     * patience: **anything but the reach is turning the reach down**, and past
     * the free ones in `cards.json` that is a heat event. The event is reported
     * rather than applied — heat lives on a fighter and this module has never
     * held one — and `heat.json` owns how much it is worth, with the other nine.
     */
    accept(slot) {
      const card = campaign.offer.find((entry) => entry.slot === slot);
      if (!card) return null;
      accepted = slot;
      ducks = slot === 'reach' ? 0 : ducks + 1;
      const duck = ducking(ducks, cards);
      return Object.freeze({
        ...card,
        /** Null when the reach was taken; there is nothing to report. */
        duck: slot === 'reach' ? null : duck,
      });
    },

    /**
     * Who is next, and which kind of fight it is.
     *
     * The card the player took, or — while nobody has taken one — the card the
     * campaign points at: the act-ending fight for a contender, and the rung
     * above for everybody else, which is exactly what this returned before there
     * were three of them. So a caller that has never heard of the offer, like
     * the headless harness, still gets a fight and still cannot be handed the
     * act from rank eleven.
     */
    get next() {
      const open = rank() <= threshold;
      const card = campaign.accepted ?? campaign.offer.find((entry) => entry.pointed) ?? null;
      const opponent = card?.opponent ?? (open ? actOpponent() : circuitOpponent());
      const isAct = card ? card.kind === 'act' : open;
      return Object.freeze({
        kind: isAct ? 'act' : 'circuit',
        /** Which of the three this is, or null when nothing was offered. */
        slot: card?.slot ?? null,
        /** What it pays, or null for a fight nobody put money on. */
        purse: card?.purse ?? null,
        opponent,
        /** The act's name when it is the act, so a caller has something to show. */
        name: isAct ? config.act.name : opponent?.name ?? null,
        /**
         * Where it is fought: the card's venue, or — for a caller that never
         * looked at the offer — where these two would fight anyway. The arena
         * host loads `venue.arena` for it. Null on a ladder with no venues.
         */
        venue: card?.venue ?? (opponent ? venueCard(ladder, opponent.id) : null),
        gate: campaign.gate,
      });
    },

    /**
     * A fight happened. Records it, moves the ladder, and returns what changed.
     *
     * `opponent` defaults to whoever was next, so the ordinary call is
     * `record({ won })`. Nothing about a loss is treated differently from a win
     * except its direction: both increment `fights`, both go in the history,
     * and both settle through the same one function on the ladder.
     */
    /**
     * A fight happened. Records it, moves the ladder, and returns what changed.
     *
     * Three outcomes, not two. `drawn` is a fight nobody won: the ladder does
     * not move, the streak breaks rather than continuing in either direction,
     * and it still counts as a fight — because it was one. `by` says *how* it
     * ended (`knockout`, `decision`, `draw`) and is carried straight through to
     * the history, so a screen can name it without guessing from `method`.
     */
    record({
      won,
      drawn = false,
      by = 'knockout',
      opponent = null,
      method = null,
      on = null,
      seconds = null,
      heat = [],
      at = null,
    } = {}) {
      const foe = ladder.find(opponent ?? campaign.next.opponent?.id);
      if (!foe || foe.id === playerId) {
        throw new Error(`no such opponent "${opponent}" to record a fight against`);
      }
      const wasAct = rank() <= threshold && foe.id === actOpponent()?.id;
      const from = rank();
      const booked = campaign.next.opponent?.id === foe.id;
      const paid = booked ? campaign.next.purse : null;
      // Where it was fought: what the caller says, else the venue on the card,
      // else wherever these two would have fought. Read *before* the result
      // moves the ladder, because the result can change who would host.
      // `false` is "nowhere": a fight in an arena picked by hand rather than
      // booked, where no room is on the line.
      const venue =
        at === false
          ? null
          : (ladder.venue?.(at) ?? (booked ? campaign.next.venue : venueCard(ladder, foe.id)));
      const where = venue?.id ?? null;

      const result = drawn
        ? ladder.drew(playerId, foe.id, { at: where, purse: paid })
        : ladder.settle(won ? playerId : foe.id, won ? foe.id : playerId, { at: where, purse: paid });
      const change = result?.venue ?? null;
      // What the player's rooms paid since their last fight, collected because
      // they turned up for this one. `{ total, rooms: [{ id, name, amount,
      // fights }] }`; total 0 for a player who holds nothing.
      const takings = result?.collected?.[playerId] ?? null;

      fights += 1;
      const ledger = meetings.get(foe.id) ?? { wins: 0, losses: 0, draws: 0 };
      meetings.set(foe.id, {
        wins: ledger.wins + (!drawn && won ? 1 : 0),
        losses: ledger.losses + (!drawn && !won ? 1 : 0),
        draws: (ledger.draws ?? 0) + (drawn ? 1 : 0),
      });
      if (drawn) {
        draws += 1;
        // Neither continued nor reversed. A run is a run of *results*, and a
        // draw is the absence of one.
        streak = 0;
      } else if (won) {
        wins += 1;
        streak = streak > 0 ? streak + 1 : 1;
      } else {
        losses += 1;
        streak = streak < 0 ? streak - 1 : -1;
      }
      // Winning the act-ending fight is the only thing that sets this, and
      // nothing unsets it. Losing it costs a place like any other loss, and
      // drawing it settles nothing at all — you have to go and win it.
      if (wasAct && won && !drawn) actComplete = true;

      const entry = Object.freeze({
        fight: fights,
        opponent: foe.id,
        name: foe.name,
        won: Boolean(won) && !drawn,
        drawn: Boolean(drawn),
        by,
        /** Which bar a decision went on, when it went to the cards. */
        on,
        act: wasAct,
        /** What the card was worth. Null for a fight nobody offered money on. */
        purse: paid,
        /** What the player's rooms paid out, collected on the way to this fight. */
        takings: takings
          ? Object.freeze({ total: takings.total, rooms: Object.freeze(takings.rooms.map((room) => ({ ...room }))) })
          : null,
        /**
         * Where it was fought, and whether the room changed hands in it:
         * `changed` is `took` (the player took it), `lost` (the player lost
         * it) or null. Names are carried so a screen reading the history a
         * week later says who, not which id.
         */
        venue: venue
          ? Object.freeze({
              id: venue.id,
              name: venue.name,
              arena: venue.arena,
              changed: change ? (change.to === playerId ? 'took' : 'lost') : null,
              from: change ? ladder.find(change.from)?.name ?? null : null,
              to: change ? ladder.find(change.to)?.name ?? null : null,
            })
          : null,
        method,
        seconds,
        from,
        rank: rank(),
        /**
         * What the crowd saw, carried out of the round before `match.reset`
         * wipes it. Heat is a per-round list and the round is over by the time
         * anybody wants to read it back.
         */
        heat: Object.freeze([...heat]),
      });
      history = [entry, ...history].slice(0, config.campaign.history);

      // A new fight is a new card. Thrown away after the entry is built, so the
      // purse the player was promised is the purse that goes in the history.
      offer = null;
      accepted = null;

      return announce(
        Object.freeze({ ...entry, moved: from - rank(), result, gate: campaign.gate })
      );
    },

    onRecord(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * The career as plain data. The ladder snapshots itself separately.
     */
    snapshot() {
      return {
        fights,
        wins,
        losses,
        draws,
        streak,
        actComplete,
        ducks,
        history: history.map((entry) => ({ ...entry, heat: [...(entry.heat ?? [])] })),
        meetings: Object.fromEntries([...meetings].map(([id, met]) => [id, { ...met }])),
      };
    },

    /**
     * Puts a saved career back.
     *
     * The counters are taken as given rather than recomputed from the history,
     * because the history is a *rolling eight* and a career of forty fights
     * cannot be added up from the last eight of them. The head-to-head ledger
     * is the same: it is kept precisely because it outlives the window.
     */
    restore(saved) {
      if (!saved || typeof saved !== 'object') return false;
      fights = Number(saved.fights) || 0;
      wins = Number(saved.wins) || 0;
      losses = Number(saved.losses) || 0;
      draws = Number(saved.draws) || 0;
      streak = Number(saved.streak) || 0;
      actComplete = Boolean(saved.actComplete);
      ducks = Number(saved.ducks) || 0;
      // The offer is not saved. It is not a thing a career *did* — it is a thing
      // on the table — and the table it was made from is the ladder, which is
      // saved. A reload makes a fresh one out of where everybody is now, which
      // is the same reason a reload puts you on the marks rather than back into
      // the round you were having (§7).
      offer = null;
      accepted = null;
      history = (saved.history ?? [])
        .slice(0, config.campaign.history)
        .map((entry) => Object.freeze({ ...entry, heat: Object.freeze([...(entry.heat ?? [])]) }));
      meetings.clear();
      for (const [id, met] of Object.entries(saved.meetings ?? {})) {
        meetings.set(id, {
          wins: Number(met?.wins) || 0,
          losses: Number(met?.losses) || 0,
          draws: Number(met?.draws) || 0,
        });
      }
      return true;
    },

    /** Wipes the record. The ladder is the ladder's own business. */
    reset() {
      fights = 0;
      wins = 0;
      losses = 0;
      draws = 0;
      streak = 0;
      history = [];
      meetings.clear();
      actComplete = false;
      ducks = 0;
      offer = null;
      accepted = null;
      return campaign;
    },
  };

  return campaign;
}
